import { CommonModule, Location } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideBanknote,
  lucideRefreshCw,
  lucideClipboardList,
  lucideShieldCheck,
  lucideTrendingUp,
  lucideTrendingDown,
  lucidePlus,
} from '@ng-icons/lucide';

import { ApiService, DynamicListComponent } from '@mm-aggregator/drf-dynamics';
import { TopupWizardComponent } from '../../components/topup-wizard/topup-wizard.component';
import { UI } from '../../components/ui';
import { MarketCurrencyService } from '../../services/market-currency.service';
import type { EntityConfig } from '../../config/entity-config';
import { ShellRoutesService } from '../../services/shell-routes.service';
import { formatHumanAmount } from '../../utils/admin-formatting';
import { goBackOrNavigate } from '../../utils/navigation';

interface TypeSummary {
  entry_type: string;
  count: number;
  total_amount: string;
}

interface WalletHubData {
  wallet: Record<string, unknown>;
  summary: {
    total_statements: number;
    by_type: TypeSummary[];
    ledger_balance: string;
    wallet_balance: string;
    difference: string;
    is_tagged_mismatch: boolean;
    mismatch_reason: string;
    tagged_statements_count: number;
  };
}

interface WalletTopupResponse {
  wallet: Record<string, unknown>;
  journal_entry_id: string;
}

/**
 * Wallet detail hub page showing balance, wallet statements, and linked journals.
 */
@Component({
  selector: 'app-wallet-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, NgIcon, DynamicListComponent, TopupWizardComponent, ...UI],
  templateUrl: './wallet-detail.component.html',
  viewProviders: [
    provideIcons({
      lucideBanknote,
      lucideRefreshCw,
      lucideClipboardList,
      lucideShieldCheck,
      lucideTrendingUp,
      lucideTrendingDown,
      lucidePlus,
        }),
  ],
})
export class WalletDetailComponent implements OnInit, OnChanges {
  @Input() entityConfig!: EntityConfig;
  @Input() id!: string;

  private readonly api = inject(ApiService);
  private readonly marketCurrencyService = inject(MarketCurrencyService);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  private readonly shellRoutes = inject(ShellRoutesService);

  /** Dynamic list API path (v1 prefix added by ApiService base URL). */
  readonly statementsListPath = 'finances/wallet-statements/';
  /** Fixed wallet filter for embedded statements list; updated when `id` changes. */
  statementExtraParams: Record<string, string> = {};

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly data = signal<WalletHubData | null>(null);
  readonly topupModalOpen = signal(false);
  readonly fundWizardOpen = signal(false);
  readonly marketCurrencyCode = signal('');
  readonly marketCurrencyId = signal('');
  readonly saving = signal(false);
  readonly actionMessage = signal<string | null>(null);

  topupAmount: string | number | null = '';

  ngOnInit(): void {
    this.syncStatementParams();
    this.loadHub();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['id']) {
      this.syncStatementParams();
      if (!changes['id'].firstChange) {
        this.loadHub();
      }
    }
  }

  private syncStatementParams(): void {
    this.statementExtraParams = { wallet: this.id };
  }

  goBack(): void {
    goBackOrNavigate(this.location, this.router, this.shellRoutes.commands('manage', this.entityConfig.key));
  }

  loadHub(): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.get<WalletHubData>(`finances/wallets/${this.id}/hub/`).subscribe({
      next: (hub) => {
        this.data.set(hub);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.error?.detail ?? err?.message ?? 'Failed to load wallet data.');
        this.loading.set(false);
      },
    });
  }

  get wallet(): Record<string, unknown> | null {
    return this.data()?.wallet ?? null;
  }

  currencyCode(): string {
    const code = this.data()?.wallet?.['currency_code'];
    return typeof code === 'string' ? code : '';
  }

  countryCode(): string {
    const code = this.data()?.wallet?.['country_code'];
    return typeof code === 'string' ? code : '';
  }

  openFundWizard(): void {
    this.actionMessage.set(null);
    const country = this.data()?.wallet?.['country_code'];
    const currency = this.currencyCode();
    if (typeof country === 'string' && currency) {
      this.marketCurrencyService.resolveMarket(currency, country).subscribe({
        next: (market) => {
          if (!market?.id) {
            this.actionMessage.set('Could not resolve market currency for this wallet.');
            return;
          }
          this.marketCurrencyCode.set(market.code);
          this.marketCurrencyId.set(market.id);
          this.fundWizardOpen.set(true);
        },
        error: () => {
          this.marketCurrencyCode.set(this.marketCurrencyService.buildMarketCode(currency, country));
          this.marketCurrencyId.set('');
          this.fundWizardOpen.set(true);
        },
      });
    } else {
      this.marketCurrencyCode.set(currency || '');
      this.marketCurrencyId.set('');
      this.fundWizardOpen.set(true);
    }
  }

  closeFundWizard(): void {
    this.fundWizardOpen.set(false);
  }

  onFundCompleted(): void {
    this.fundWizardOpen.set(false);
    this.actionMessage.set($localize`:@@wallet.fund.success:Composite funding recorded.`);
    this.loadHub();
  }

  openTopupModal(): void {
    this.actionMessage.set(null);
    this.topupAmount = '';
    this.topupModalOpen.set(true);
  }

  closeTopupModal(): void {
    this.topupModalOpen.set(false);
  }

  submitTopup(): void {
    const amount = String(this.topupAmount ?? '').trim();
    if (!amount || Number(amount) <= 0) {
      this.actionMessage.set('Enter a positive amount.');
      return;
    }
    this.saving.set(true);
    this.actionMessage.set(null);
    this.api.post<WalletTopupResponse>(`finances/wallets/${this.id}/topup/`, { amount }).subscribe({
      next: () => {
        const code = this.currencyCode();
        this.actionMessage.set(`Topup of ${formatHumanAmount(amount, code)} recorded.`);
        this.closeTopupModal();
        this.loadHub();
        this.saving.set(false);
      },
      error: (err) => {
        const detail = err?.error?.detail ?? err?.error?.amount?.[0] ?? err?.message;
        this.actionMessage.set(detail || 'Failed to top up wallet.');
        this.saving.set(false);
      },
    });
  }

  formatNumber(val: string | number): string {
    const n = typeof val === 'string' ? parseFloat(val) : val;
    if (isNaN(n)) return '0.00';
    return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  }

  onStatementRowSelect(row: Record<string, unknown>): void {
    const statementId = row['id'];
    if (statementId != null && String(statementId) !== '') {
      void this.router.navigate(this.shellRoutes.commands('manage', 'wallet-statements', String(statementId)));
    }
  }

  entryTypeClass(entryType: string): string {
    const map: Record<string, string> = {
      DEPOSIT: 'bg-success-bg text-success-fg',
      PAYOUT: 'bg-danger-bg text-danger-fg',
      REFUND: 'bg-warning-bg text-warning-fg',
      REMITTANCE_DEBIT: 'bg-danger-bg text-danger-fg',
      REMITTANCE_CREDIT: 'bg-info-bg text-info-fg',
      ADJUSTMENT: 'bg-row-hover text-brand',
      SETTLEMENT: 'bg-success-bg text-success',
      TRANSACTION_STATUS_CHECK: 'bg-surface-muted text-fg-secondary',
    };
    return map[entryType] ?? 'bg-surface-muted text-muted';
  }

  isPositive(amount: string): boolean {
    return parseFloat(amount) >= 0;
  }
}
